import { Fragment, useEffect } from 'react';

import {
  SecondaryNavIdentifier,
  TertiaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';
import { ResponsiveDesignProvider } from '@zola/zola-ui/src/contexts/ResponsiveDesignContext/ResponsiveDesignProvider';
import { useForceUpdateOnWindowResize } from '@zola/zola-ui/src/hooks';
import { COLORS3, FONT } from '@zola/zola-ui/src/styles/emotion';
import MEDIA_QUERY_V2, { SCREEN_MASSIVE } from '@zola/zola-ui/src/styles/emotion/mediaQueryV2';

import styled from '@emotion/styled';
import cx from 'classnames';
import { Provider } from 'react-redux';

import ClientApp from '~/ClientApp';
import ClientRouter from '~/ClientRouter';
import MainLayout from '~/components/common/layouts/MainLayout/MainLayout';
import { UserContextProvider } from '~/contexts/UserContext';
import { SearchResultsPageProvider } from '~/pages/couples/explore/components/SearchResults/contexts/SearchResultsPageProvider';
import { VendorSearchNavigationProvider } from '~/pages/couples/explore/components/SearchResults/contexts/VendorSearchNavigationProvider';
import { getStore } from '~/store';

import SearchResultsPageModalsAndToasts from '../../SearchResultsPageModalsAndToasts';
import { ExpertAdviceForSRP } from '../ExpertAdvice/ExpertAdviceForSrp';
import { PageSection } from '../PageSection/PageSection';
import { AdjacentVendors } from './components/AdjacentVendors/AdjacentVendors';
import { MobileSearchFilters } from './components/PreAuth/MobileSearchFilters';
import { PageHeading } from './components/PreAuth/PageHeading';
import { PreAuthSearchResults } from './components/PreAuth/PreAuthSearchResults';
import { RealWeddings } from './components/RealWeddings/RealWeddings';
import {
  SearchBreadcrumb,
  useSearchBreadcrumbs,
} from './components/SearchBreadcrumb/SearchBreadcrumb';
import { SRPSearchBarProvider } from './components/SearchControls/SRPSearchBarContext';
import { SearchResultsHead } from './components/SearchResultsHead/SearchResultsHead';
import { SeoContentBlock } from './components/SeoContentBlock/SeoContentBlock';
import { useSearchResultsPageContext } from './contexts/SearchResultsPageContext';
import { VendorGridCardsProvider } from './contexts/VendorGridCardsProvider';
import { SearchResultsPageProps } from './types/props';

import styles from './searchResultsPageLayout.module.less';

const PageWrapper = styled.div`
  ${MEDIA_QUERY_V2.DESKTOP_MASSIVE} {
    display: flex;
    flex-direction: column;
    align-items: center;

    > div,
    > section {
      width: 100%;
      max-width: ${SCREEN_MASSIVE}px;
      border-left: 1px solid ${COLORS3.BLACK_030};
      border-right: 1px solid ${COLORS3.BLACK_030};
    }
  }
`;

const Divider = styled.div`
  ${MEDIA_QUERY_V2.EXCLUDE_DESKTOP} {
    display: none;
  }
  hr {
    padding: 0;
    margin: 0;
    margin-top: 24px;
    border-color: ${COLORS3.BLACK_030};
  }
`;

const BackLink = styled(LinkV2)`
  font-weight: 400;
  font-size: ${FONT.FONT_SIZE_EXTRA_SMALL}; 
  line-height: 22px;

  &&& {
    color: ${COLORS3.BLACK_075};
    text-decoration: underline;
    &:hover {
      color: ${COLORS3.BLACK_075};
      text-decoration: underline;
    }  
  }
`;

const MobileWebBreadcrumb = () => {
  const breadcrumbList = useSearchBreadcrumbs();

  if (breadcrumbList.length < 2) {
    return <Fragment />;
  }

  const item = breadcrumbList[breadcrumbList.length - 2];

  return (
    <nav style={{ paddingTop: '16px' }} className={styles.onlyMobile}>
      <ul>
        <li>
          <BackLink arrow arrowPosition="prefix" href={item.to}>
            {item.label}
          </BackLink>
        </li>
      </ul>
    </nav>
  );
};

const DesktopBreadcrumb = styled.div`
  ${MEDIA_QUERY_V2.MOBILE} {
    display: none;
  }
`;

const Breadcrumb = () => {
  return (
    <PageSection noPadding>
      <MobileWebBreadcrumb />
      <DesktopBreadcrumb>
        <SearchBreadcrumb />
      </DesktopBreadcrumb>
    </PageSection>
  );
};

const ScrollTop = () => {
  const { canonicalUrl } = useSearchResultsPageContext();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [canonicalUrl]);

  return <Fragment />;
};

/**
 * Pre-auth winner of an SRP A/B test variation:
 * - Grid of cards to list of cards
 * - Card presentation is wider, includes a review and a vendor description
 * - Location of favorite button is moved
 * - On desktop, a left rail of filter controls is used
 * - H1 header is changed
 *
 * Control was vs a grid of cards
 */
export const PreAuthSearchResultsPage = ({
  identifiedAsVendor,
  navHtml,
  parsedUrl,
  userAgent,
  userContext,
  ...rest
}: SearchResultsPageProps) => {
  useForceUpdateOnWindowResize();

  return (
    <UserContextProvider userContext={userContext}>
      <ResponsiveDesignProvider userAgent={userAgent}>
        <Provider store={getStore()}>
          <MainLayout
            activeSecondaryLink={SecondaryNavIdentifier.EXPLORE_VENDORS}
            activeTertiaryLink={
              parsedUrl.vendorSearch.selectedTaxonomy?.key as TertiaryNavIdentifier
            }
            ssrNavHtml={navHtml}
            className={cx(styles.mainLayout)}
            id="main"
          >
            <PageWrapper>
              <SearchResultsPageProvider
                {...rest}
                identifiedAsVendor={identifiedAsVendor}
                parsedUrl={parsedUrl}
              >
                <ScrollTop />
                <VendorSearchNavigationProvider>
                  <SearchResultsHead isPreAuthSrp />
                  {/* 
                // No top picks in the pre-auth SRP
                <TopPicksSection
                  fromSrp
                  isPricingRatingTest1={isPricingRatingTest1}
                  isPricingRatingTest2={isPricingRatingTest2}
                /> */}
                  <Breadcrumb />
                  <SRPSearchBarProvider>
                    <PageHeading />
                  </SRPSearchBarProvider>
                  <Divider>
                    <hr />
                  </Divider>

                  <MobileSearchFilters />
                  <VendorGridCardsProvider>
                    <PreAuthSearchResults />
                  </VendorGridCardsProvider>
                  <AdjacentVendors className={styles.withTopBorder} />
                  <ExpertAdviceForSRP className={styles.withTopBorder} />
                  <RealWeddings className={styles.withTopBorder} />
                  <PageSection className={styles.withTopBorder}>
                    <SeoContentBlock />
                  </PageSection>
                </VendorSearchNavigationProvider>

                <ClientApp useStore={false}>
                  <ClientRouter>
                    <SearchResultsPageModalsAndToasts />
                  </ClientRouter>
                </ClientApp>
              </SearchResultsPageProvider>
            </PageWrapper>
          </MainLayout>
        </Provider>
      </ResponsiveDesignProvider>
    </UserContextProvider>
  );
};
